import React, { useCallback } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { ColorDropdown, ColorPanelProps } from "@mapmycustomers/ui";

import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import getContrastColor from "@app/util/colors/getContrastColor";

import styles from "./ColorPicker.module.scss";

interface Props extends Pick<ColorPanelProps, "showColorSpace" | "showInput"> {
  color: AnyColor | null;
  editable?: boolean;
  onColorChange: (color: AnyColor) => void;
  onOpacityChange?: (opacity: number) => void;
  opacity?: number;
}

const ColorPicker: React.FC<Props> = ({
  color,
  editable = true,
  onColorChange,
  onOpacityChange,
  opacity,
  showColorSpace,
  showInput,
}) => {
  const intl = useIntl();

  const bgColor = anyColorToHex(color ?? Color.BLACK);
  const fgColor = getContrastColor(bgColor) === "white" ? colors.white : colors.slate;

  // need this callback to provide it to debounce listener
  const handleOpacityChange = useCallback(
    (opacity: number) => onOpacityChange?.(opacity),
    [onOpacityChange]
  );

  // we need this to show instant opacity changes in the picker, but to save
  // opacity with debounce
  // NOTE: technically, we need a useEffect to update this state when territory/group is changed outside
  // of this component, but this should not normally happen
  const [innerOpacity, setInnerOpacity] = useStateWithDebouncedListener(
    [handleOpacityChange, 250],
    opacity
  );
  // same note as for innerOpacity
  const [innerColor, setInnerColor] = useStateWithDebouncedListener(
    [onColorChange, 250],
    color ?? Color.BLACK
  );

  return (
    <Tooltip
      title={intl.formatMessage({
        id: "map.colorPicker.tooltip",
        defaultMessage: "Change Color",
        description: "Color picker tooltip on the Territory/Group page",
      })}
    >
      <div className={cn(styles.container, { [styles.editable]: editable })}>
        <ColorDropdown
          colorPanelProps={{
            showColorSpace,
            showInput,
            showNamedColors: true,
            showOpacitySelector: isDefined(opacity),
          }}
          onChange={setInnerColor}
          onOpacityChange={setInnerOpacity}
          opacity={innerOpacity}
          value={innerColor}
        >
          <div className={styles.content}>
            <div
              className={styles.color}
              style={{ backgroundColor: bgColor, borderColor: fgColor }}
            >
              <FontAwesomeIcon className={styles.icon} icon={faPen} style={{ color: fgColor }} />
            </div>
            {isDefined(onOpacityChange) && isDefined(opacity) && (
              <span>
                {intl.formatNumber(opacity, { maximumFractionDigits: 0, style: "percent" })}
              </span>
            )}
          </div>
        </ColorDropdown>
      </div>
    </Tooltip>
  );
};

export default ColorPicker;
